import React, { useEffect, useState, useCallback } from 'react'
import { Col, Row, Card, Table, Button, Spinner } from '@themesberg/react-bootstrap'
import { getAllDevices, deleteDevice } from '../../../../controller/api/device'
import { useCookies } from 'react-cookie';
import { calculatePage } from '../../../../helper/helper';
import Pagination from '../../../../components/Pagination';
import EmployeeSelector from '../../../../components/admin/EmployeeSelector';
import AddDeviceModal from '../../../../components/admin/AddDeviceModal';
import { getDeviceStatusColor, getDeviceStatusText } from '../../../../helper/deviceStatus';

const TableRow = (props) => {
  const { code, status, connectedEmployee, onAssignEmployee, onDelete, id, isOnline, lastHeartbeat } = props

  const renderOnlineStatus = () => {
    if (isOnline) {
      return <span className="badge bg-success">Online</span>
    }

    if (!lastHeartbeat) {
      return <span className="badge bg-secondary">Chưa xác định</span>
    }

    const lastSeen = new Date(lastHeartbeat)
    const formatted = isNaN(lastSeen.valueOf()) ? '' : lastSeen.toLocaleString()

    return (
      <div className="d-flex flex-column">
        <span className="badge bg-danger mb-1">Offline</span>
        {formatted && <span className="text-muted" style={{ fontSize: '0.75rem' }}>Lần cuối online: {formatted}</span>}
      </div>
    )
  }

  return (
    <tr>
      <td>{code}</td>
      <td className={getDeviceStatusColor(status)} style={{ width: 200 }}>
        {getDeviceStatusText(status)}
      </td>
      <td style={{ width: 160 }}>
        {renderOnlineStatus()}
      </td>
      <td style={{ width: 200 }}>
        {connectedEmployee ? (
          <span className="badge bg-primary">{connectedEmployee}</span>
        ) : (
          <span className="text-muted">Chưa kết nối</span>
        )}
      </td>
      <td style={{ width: 220 }}>
        <Button
          variant="outline-primary"
          size="sm"
          className="me-2"
          onClick={() => onAssignEmployee(id, code, connectedEmployee)}
        >
          {connectedEmployee ? '🔄 Đổi' : '👤 Gán'}
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => onDelete(id, code)}
        >
          🗑 Xoá
        </Button>
      </td>
    </tr>
  );
};

export const DeviceTable = () => {

  const [data, setData] = useState({})

  const [page, setPage] = useState(0)

  const [loaded, setLoaded] = useState(false)

  const [cookies] = useCookies(['lottmekong'])

  // Employee Selector state
  const [showEmployeeSelector, setShowEmployeeSelector] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState(null)

  const [showAddDevice, setShowAddDevice] = useState(false)

  const getAllFunc = useCallback(async () => {
    setLoaded(false)
    const response = await getAllDevices(cookies.lottmekong, page)

    if (response) {
      const res = {
        list: response.data,
        total: response.total,
        totalPage: calculatePage(response.total)
      }

      setData(res || {})
    }
    setLoaded(true)
  }, [cookies.lottmekong, page])

  useEffect(() => {
    getAllFunc()
  }, [getAllFunc])

  const handleAssignEmployee = (deviceId, deviceCode, currentEmployee) => {
    setSelectedDevice({
      id: deviceId,
      code: deviceCode,
      currentEmployee: currentEmployee
    })
    setShowEmployeeSelector(true)
  }

  const handleAssignSuccess = () => {
    // Reload data after successful assignment
    getAllFunc()
  }

  const handleDelete = async (deviceId, deviceCode) => {
    const confirmed = window.confirm(`Xoá thiết bị "${deviceCode}"? Thiết bị sẽ tự được tạo lại nếu USB còn ghim vào.`)
    if (!confirmed) return

    try {
      const result = await deleteDevice(cookies.lottmekong, deviceId)
      if (result) {
        getAllFunc()
      } else {
        window.alert('Xoá thiết bị thất bại')
      }
    } catch (error) {
      window.alert('Xoá thiết bị thất bại')
    }
  }

  return (
    <>
      <Card border="light" className="shadow-sm">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5>USB ({data.total})</h5>
            </Col>
            <Col xs="auto">
              <Button variant="primary" size="sm" onClick={() => setShowAddDevice(true)}>
                ➕ Thêm thiết bị
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Table responsive className="align-items-center table-flush">
          <thead className="thead-light">
            <tr>
              <th scope="col">Mã</th>
              <th scope="col">Trạng thái</th>
              <th scope="col">Kết nối</th>
              <th scope="col">Nhân viên in vé</th>
              <th scope="col">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {!loaded ? (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  <Spinner animation="border" className="me-2" />
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : data.list && data.list.length > 0 ? (
              data.list.map(pv => <TableRow
                token={cookies.lottmekong}
                key={`device-status-${pv.id}`}
                {...pv}
                connectedEmployee={pv.connectedEmployee}
                onAssignEmployee={handleAssignEmployee}
                onDelete={handleDelete}
              />)
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  Không có thiết bị nào
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <Card.Footer className='flex-row'>
          <Row className="align-items-center">
            <Col>
              <Pagination
                totalPage={data.totalPage}
                currentPage={page}
                onPageChange={(i) => {
                  setPage(i)
                }}
              />
            </Col>
          </Row>
        </Card.Footer>
      </Card>

      <AddDeviceModal
        show={showAddDevice}
        onHide={() => setShowAddDevice(false)}
        onAddSuccess={getAllFunc}
      />

      {/* Employee Selector Modal */}
      {selectedDevice && (
        <EmployeeSelector
          show={showEmployeeSelector}
          onHide={() => setShowEmployeeSelector(false)}
          deviceId={selectedDevice.id}
          deviceCode={selectedDevice.code}
          currentEmployee={selectedDevice.currentEmployee}
          onAssignSuccess={handleAssignSuccess}
        />
      )}
    </>
  );
};
